import React from 'react';
import PropTypes from 'prop-types';
import Helmet from 'react-helmet';
import styled from 'styled-components';
import Img from 'react-image';
import { withRouteData } from 'react-static';

import ArticleList from '../global/ArticleList';
import ContactHeader from '../global/typography/ContactHeader';
import H1Title from '../global/typography/H1Title';
import P from '../global/typography/P';
import Page from '../global/Page';
import RelatedList from './RelatedList';
import SmallButton from '../global/Button/SmallButton';

import { formatDate } from '../../globalUtils';

const Contact = styled(ContactHeader)`
  font-size: 18px;
  margin-bottom: 25px;
  font-weight: ${props => props.theme.fontWeight.regular};
  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    font-size: 14px;
  }
`;

const Header = styled.h1`
  color: ${props => props.theme.color.bgSecondary};
  font-size: 38px;
  line-height: 1.2;
  text-transform: uppercase;
  font-family: ${props => props.theme.font.sourceSans};
  margin-bottom: 45px;
  @media (max-width: 1024px) {
    font-size: 28px;
  }
  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    color: ${props => props.theme.color.bgSecondary};
    font-size: 28px;
    font-weight: ${props => props.theme.fontWeight.bold};
    letter-spacing: 0.02;
    line-height: 28px;
    margin-bottom: 35px;
  }
`;

const Info = styled(P)`
  margin-bottom: 27px;
`;

const Image = styled(Img)`
  object-fit: cover;
  width: 100%;
`;

const ImageWrapper = styled.div`
  margin: 0 auto 40px auto;
  max-height: 524px;
  width: 65%;
  overflow: hidden;
  @media (max-width: 1024px) {
    width: 55%;
  }
  @media (max-width: ${props => props.theme.deviceWidth.largePhone}) {
    margin-bottom: 10px;
    width: 100%;
  }
`;

const PageWrapper = styled.div`
  margin: 0 auto;
  max-width: 1210px;
  padding: 15px;
  @media (min-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    padding: 40px 20px;
  }
  @media print {
    padding-top: 10px;
  }
`;

const PrintButton = styled(SmallButton)`
  margin-bottom: 70px;
  @media (max-width: ${props => props.theme.deviceWidth.mobilePhone}) {
    display: none;
  }
  @media print {
    display: none;
  }
`;

const SubTitle = styled(H1Title)`
  font-size: 28px;
  margin-top: 0;
`;

const RelatedArticles = styled(ArticleList)`
  @media print {
    display: none;
  }
`;

class NewsArticle extends React.PureComponent {
  render() {
    const { article, recentNews } = this.props;
    return (
      <Page>
        <Helmet
          title={article.seo ? article.seo.title : 'News Article'}
          meta={[
            { content: 'NewsArticle', property: 'og:title' },
            {
              property: 'description',
              content: article.seo
                ? article.seo.description
                : article.mainHeadline
            }
          ]}
        />
        <PageWrapper pad>
          <div>
            <Header>{article.mainHeadline}</Header>
            {article.imageUrl && (
              <ImageWrapper>
                <Image alt={article.mainHeadline} src={article.imageUrl} />
              </ImageWrapper>
            )}
            <SubTitle>{article.subHeader}</SubTitle>
            <Contact>
              by {article.author} | {formatDate(article.dateCreated, true)}
            </Contact>
            <Info dangerouslySetInnerHTML={{ __html: article.story }} />
            <PrintButton onClick={() => window.print()}>
              Print Article
            </PrintButton>
          </div>
          <RelatedArticles
            articles={recentNews.filter(a => a.id !== article.id).slice(0, 3)}
            title="Recent News"
          />
          <RelatedList data={article.documents} />
          <RelatedList data={article.products} isProduct />
        </PageWrapper>
      </Page>
    );
  }
}

NewsArticle.propTypes = {
  article: PropTypes.object,
  recentNews: PropTypes.array
};

export default withRouteData(NewsArticle);
